import type { PDFForm } from 'pdf-lib';
import { PDFDocument } from 'pdf-lib';
import type { CharacterSheetData, PdfExportOptions, PdfFieldMapping } from './types';
import { PdfExportError } from './types';
import { getFieldMapper } from './fieldMappings';
import { getTemplate, getDefaultTemplateForRuleset, type TemplateConfig } from './templateRegistry';
import { renderHtmlToPdf, loadHtmlTemplate } from './clientPdfRenderer';

// Default template path for backward compatibility
const DEFAULT_TEMPLATE_PATH = '/templates/character-sheet.pdf';

export class CharacterSheetPdfGenerator {
  /** Cache templates by path to avoid re-fetching */
  private templateCache: Map<string, ArrayBuffer> = new Map();

  /**
   * Load a template from public assets
   * Uses cache to avoid re-fetching the same template
   */
  async loadTemplate(templatePath: string): Promise<ArrayBuffer> {
    // Check cache first
    if (this.templateCache.has(templatePath)) {
      return this.templateCache.get(templatePath)!;
    }

    try {
      const response = await fetch(templatePath);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const templateBytes = await response.arrayBuffer();
      this.templateCache.set(templatePath, templateBytes);
      return templateBytes;
    } catch (error) {
      throw new PdfExportError(
        `Failed to load character sheet template: ${templatePath}`,
        'TEMPLATE_LOAD_FAILED',
        error as Error
      );
    }
  }

  /**
   * Resolve the template configuration from options
   * Returns template config and the path to use
   */
  private resolveTemplate(
    options: PdfExportOptions,
    rulesetId?: string
  ): { config: TemplateConfig | null; path: string; mappingId: string } {
    // If templateId is provided, look it up
    if (options.templateId) {
      const config = getTemplate(options.templateId);
      if (config) {
        return {
          config,
          path: config.templatePath,
          mappingId: config.fieldMappingId,
        };
      }
      // Template ID not found, fall through to defaults
      console.warn(`Template not found: ${options.templateId}, using default`);
    }

    // Try to get default template for ruleset
    if (rulesetId) {
      const defaultConfig = getDefaultTemplateForRuleset(rulesetId);
      if (defaultConfig) {
        return {
          config: defaultConfig,
          path: defaultConfig.templatePath,
          mappingId: defaultConfig.fieldMappingId,
        };
      }
    }

    // Ultimate fallback: WotC template
    return {
      config: null,
      path: DEFAULT_TEMPLATE_PATH,
      mappingId: 'wotc',
    };
  }

  /**
   * Generate a filled PDF from character data
   *
   * @param characterData - The character data to fill into the PDF
   * @param options - Export options including templateId
   * @param rulesetId - Optional ruleset ID for default template selection
   */
  async generatePdf(
    characterData: CharacterSheetData,
    options: PdfExportOptions = {},
    rulesetId?: string
  ): Promise<Uint8Array> {
    // Resolve which template and mapping to use
    const { config, path, mappingId } = this.resolveTemplate(options, rulesetId);

    // Route based on render method
    if (config?.renderMethod === 'html-to-pdf') {
      return this.generateHtmlToPdf(characterData, config, mappingId);
    }

    // Default: fillable PDF approach
    return this.generateFillablePdf(characterData, path, mappingId, options);
  }

  /**
   * Generate PDF via client-side HTML rendering (html2canvas + jsPDF)
   */
  private async generateHtmlToPdf(
    characterData: CharacterSheetData,
    config: TemplateConfig,
    mappingId: string
  ): Promise<Uint8Array> {
    // Get the appropriate field mapper
    const fieldMapper = getFieldMapper(mappingId);

    // Get field mappings from character data
    const fieldMappings = fieldMapper(characterData);

    // Convert mappings to simple key-value object
    const templateData: Record<string, string> = {};
    for (const mapping of fieldMappings) {
      templateData[mapping.fieldName] = String(mapping.value ?? '');
    }

    try {
      // Load the HTML template
      const templateId = config.serverTemplateId || config.id;
      const templateHtml = await loadHtmlTemplate(templateId);

      // Render to PDF client-side
      return await renderHtmlToPdf({
        characterData: templateData,
        templateHtml,
      });
    } catch (error) {
      throw new PdfExportError(
        'Failed to generate PDF',
        'CLIENT_GENERATION_FAILED',
        error as Error
      );
    }
  }

  /**
   * Generate PDF by filling existing PDF form fields
   */
  private async generateFillablePdf(
    characterData: CharacterSheetData,
    templatePath: string,
    mappingId: string,
    options: PdfExportOptions
  ): Promise<Uint8Array> {
    // Load the template
    const templateBytes = await this.loadTemplate(templatePath);

    try {
      // Load the PDF document
      const pdfDoc = await PDFDocument.load(templateBytes);
      const form = pdfDoc.getForm();

      // Get the appropriate field mapper
      const fieldMapper = getFieldMapper(mappingId);

      // Get field mappings from character data
      const fieldMappings = fieldMapper(characterData);

      // Fill each field
      for (const mapping of fieldMappings) {
        this.fillField(form, mapping);
      }

      // Optionally flatten (makes non-editable)
      if (options.flatten) {
        form.flatten();
      }

      // Serialize the PDF
      return await pdfDoc.save();
    } catch (error) {
      if (error instanceof PdfExportError) {
        throw error;
      }
      throw new PdfExportError(
        'Failed to generate PDF',
        'GENERATION_FAILED',
        error as Error
      );
    }
  }

  /**
   * Fill a single field in the PDF form
   */
  private fillField(form: PDFForm, mapping: PdfFieldMapping): void {
    try {
      if (mapping.fieldType === 'text') {
        const field = form.getTextField(mapping.fieldName);
        if (field) {
          field.setText(String(mapping.value));
        }
      } else if (mapping.fieldType === 'checkbox') {
        const field = form.getCheckBox(mapping.fieldName);
        if (field) {
          if (mapping.value) {
            field.check();
          } else {
            field.uncheck();
          }
        }
      }
    } catch (error) {
      // Field may not exist in template - log and continue
      // This is expected for some fields that may vary between template versions
      console.warn(`PDF field not found or error: ${mapping.fieldName}`, error);
    }
  }

  /**
   * Trigger download of the generated PDF
   *
   * @param characterData - The character data to fill into the PDF
   * @param options - Export options including templateId and filename
   * @param rulesetId - Optional ruleset ID for default template selection
   */
  async downloadPdf(
    characterData: CharacterSheetData,
    options: PdfExportOptions = {},
    rulesetId?: string
  ): Promise<void> {
    const pdfBytes = await this.generatePdf(characterData, options, rulesetId);

    const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    // Create filename from character name
    const safeName = (characterData.characterName || 'character')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    const filename = options.filename || `${safeName}-character-sheet.pdf`;

    // Create and click a download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL
    URL.revokeObjectURL(url);
  }

  /**
   * Clear the template cache
   * Useful for testing or when templates are updated
   */
  clearCache(): void {
    this.templateCache.clear();
  }
}

// Singleton instance for reuse
let generatorInstance: CharacterSheetPdfGenerator | null = null;

export function getGenerator(): CharacterSheetPdfGenerator {
  if (!generatorInstance) {
    generatorInstance = new CharacterSheetPdfGenerator();
  }
  return generatorInstance;
}
